import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import styles from './PreFilterForm.module.scss';

import MultiSelectField from '../../../components/inputs/MultiSelectField';
import { FormikSingleSelect as SingleSelectField } from '../../../components/inputs/SingleSelectField';
import CheckBox from '../../../components/inputs/CheckBox';
import { validations } from '../../../../helperFunctions';
import useTranslator from '../../../../hooks/useTranslator';
import useFilterDataAsUrl from '../../../../hooks/useFilterDataAsUrl';
import useDidMountEffect from '../../../../hooks/useDidMountEffect';
import {
  buildFilterOptions,
  buildFilters,
  getPrefilterValidationSchema,
  openFilterDropdown,
  prefFilterInitialValues,
  buildSubmitValues,
  getPrimaryConditionsOptions,
  getSecondaryConditionsOptions,
} from '../../../../components/call_center/pre_filter/helper';

const NUM_OF_PAGES = 3;

const PreFilterForm = ({ filterFieldsData = {}, onSubmit, locale = 'en' }) => {
  const [, params, updateURL] = useFilterDataAsUrl();
  const [currentPage, setCurrentPage] = useState(1);
  const translator = useTranslator(locale);
  const history = useHistory();

  // Validation schema
  const validationSchema = getPrefilterValidationSchema(translator);

  // Backend defaults (lowest priority)
  const backendDefaults = useMemo(() => {
    const data = filterFieldsData || {};
    return prefFilterInitialValues(data);
  }, [filterFieldsData]);

  // Extract form data from URL parameters
  const urlFormData = useMemo(() => {
    const urlData = {};
    Object.keys(backendDefaults).forEach((key) => {
      if (params[key] !== undefined) {
        const backendDefault = backendDefaults[key];
        const paramValue = params[key];

        // If backend default has `selected` and param is primitive, wrap it and add selected
        if (
          backendDefault &&
          typeof backendDefault === 'object' &&
          'selected' in backendDefault &&
          (typeof paramValue !== 'object' ||
            paramValue === null ||
            !('selected' in paramValue))
        ) {
          urlData[key] = { ...paramValue, selected: backendDefault.selected };
        } else {
          urlData[key] = paramValue;
        }
      }
    });
    return urlData;
  }, [params, backendDefaults]);

  // Final initial values (URL overrides backend defaults)
  const initialValues = useMemo(
    () => ({
      ...backendDefaults,
      ...urlFormData,
    }),
    [backendDefaults, urlFormData],
  );

  // Filter options from API data
  const filterOptions = useMemo(
    () => buildFilterOptions(filterFieldsData),
    [filterFieldsData],
  );

  // Unremovable defaults for primary conditions (from API)
  const primaryMinSelected =
    (filterFieldsData && filterFieldsData.primary_conditions?.default) || [];

  // Filter configuration
  const filters = useMemo(
    () => buildFilters(translator, filterOptions),
    [translator, filterOptions],
  );

  // Handle filter selection
  const handleFilterSelect = (value, filterKey) => {
    // Automatically open the dropdown for the newly selected filter
    if (value) {
      openFilterDropdown(filterKey);
    }
  };

  // Check if all selected filters have values
  const areSelectedFiltersValid = (values) =>
    filters.every((filter) => {
      const filterValue = values[filter.key];
      if (!filterValue?.selected) return true; // Skip unselected filters

      if (filter.type === 'multi') {
        return filterValue?.values && filterValue.values.length > 0;
      }
      return filterValue?.value && validations.isNotNull(filterValue.value);
    });

  // Check if form is valid for current page
  const isFormValidForPage = (values) => {
    switch (currentPage) {
      case 1:
        return (
          values.primary_conditions && values.primary_conditions.length > 0
        );
      case 2:
        return true; // Secondary conditions are optional
      case 3:
        return areSelectedFiltersValid(values);
      default:
        return false;
    }
  };

  const urlPage = parseInt(params.currentPage, 10) || 1;

  // Initialize state from URL parameters
  useEffect(() => {
    // Initialize current page from URL
    if (urlPage && urlPage >= 1 && urlPage <= NUM_OF_PAGES) {
      setCurrentPage(urlPage);
    }
  }, [urlPage]);

  // Update URL when page changes
  const handlePageChange = useCallback(
    (newPage, formValues = {}) => {
      const replaceState = newPage === currentPage;
      setCurrentPage(newPage);
      updateURL(
        {
          ...formValues,
          currentPage: newPage,
        },
        replaceState,
      );
    },
    [updateURL, currentPage],
  );

  const handleNext = (values, setSubmitting) => {
    const newPage =
      currentPage === NUM_OF_PAGES ? NUM_OF_PAGES : currentPage + 1;
    handlePageChange(newPage, values);
    if (currentPage >= NUM_OF_PAGES) {
      // Submit the form
      if (onSubmit) {
        const cleanedValues = buildSubmitValues(
          values,
          filterFieldsData,
          filters,
          null,
        );

        onSubmit(cleanedValues);
      }
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentPage > 1) {
      history.goBack();
    }
  };

  const FormObserver = () => {
    const { values } = useFormikContext();
    useDidMountEffect(() => {
      updateURL({ ...values, currentPage }, true);
    }, [values]);
    return null;
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        handleNext(values, setSubmitting);
      }}
    >
      {({ values, setFieldValue }) => (
        <Form className={styles.form}>
          <FormObserver />
          {/* Page 1: Primary Conditions */}
          <div
            className={clsx(
              styles.section,
              currentPage === 1 || 'display-none',
            )}
          >
            <h1 className={clsx('prompt', styles.sectionTitle)}>
              {translator.questions.main_reason_for_calling}
            </h1>
            <div className={styles.requiredText}>
              {translator.prefilter_required}
            </div>
            <div className={styles.sectionContent}>
              <MultiSelectField
                name="primary_conditions"
                label=""
                options={getPrimaryConditionsOptions(values, filterOptions)}
                minSelected={primaryMinSelected}
                placeholder={translator.prefilter_select_multiple}
                lucetTheme
                customClass={styles.primaryConditionsSelect}
              />
            </div>
          </div>

          {/* Page 2: Secondary Conditions */}
          <div
            className={clsx(
              styles.section,
              currentPage === 2 || 'display-none',
            )}
          >
            <h1 className={clsx('prompt', styles.sectionTitle)}>
              {translator.questions.any_other_bh_issue_aware_of}
            </h1>
            <div className={styles.optionalText}>
              {translator.prefilter_optional}
            </div>
            <div className={styles.sectionContent}>
              <MultiSelectField
                name="secondary_conditions"
                label=""
                options={getSecondaryConditionsOptions(values, filterOptions)}
                placeholder={translator.prefilter_select_multiple}
                lucetTheme
                customClass={styles.secondaryConditionsSelect}
              />
            </div>
          </div>

          {/* Page 3: Filters */}
          <div
            className={clsx(
              styles.section,
              currentPage === 3 || 'display-none',
            )}
          >
            <h1 className={clsx('prompt', styles.sectionTitle)}>
              {translator.questions.important_to_you_in_a_provider}
            </h1>
            <div className={styles.optionalText}>
              {translator.prefilter_optional}
            </div>
            <div className={styles.filtersContainer}>
              {filters.map((filter) => {
                const isSelected =
                  values[filter.key]?.selected === true || false;
                const hasValue =
                  values[filter.key] &&
                  (filter.type === 'multi'
                    ? values[filter.key].values?.length > 0
                    : values[filter.key].value);

                const filterClass = clsx(
                  styles.filterRow,
                  isSelected && styles.filterSelected,
                  hasValue && styles.filterHasValue,
                );

                return (
                  <div
                    key={filter.key}
                    className={filterClass}
                    data-filter-key={filter.key}
                  >
                    <CheckBox
                      name={`${filter.key}.selected`}
                      onChange={(value) => {
                        handleFilterSelect(value, filter.key);
                      }}
                      className={styles.filterCheckbox}
                      label={filter.label}
                      labelClass={styles.filterLabel}
                      subLabel={filter.subtitle}
                      subLabelClass={styles.filterSubtitle}
                    />

                    {isSelected && (
                      <React.Fragment>
                        <div className={styles.filterControls}>
                          <div data-form-control="true">
                            {filter.type === 'multi' ? (
                              <MultiSelectField
                                name={`${filter.key}.values`}
                                options={filter.options}
                                placeholder="Select one or more"
                                lucetTheme
                                customClass={styles.filterSelect}
                                onChange={(newValue) => {
                                  if (
                                    !newValue ||
                                    validations.isNull(newValue.value) ||
                                    newValue.value.length === 0
                                  ) {
                                    setFieldValue(
                                      `${filter.key}.must_have`,
                                      false,
                                    );
                                  }
                                }}
                              />
                            ) : (
                              <SingleSelectField
                                name={`${filter.key}.value`}
                                options={filter.options}
                                placeholder="Select option"
                                lucetTheme
                                customClass={styles.filterSelect}
                                onChange={(newValue) => {
                                  if (
                                    !newValue ||
                                    validations.isNull(newValue.value)
                                  ) {
                                    setFieldValue(
                                      `${filter.key}.must_have`,
                                      false,
                                    );
                                  }
                                }}
                              />
                            )}
                          </div>

                          {hasValue && (
                            <div
                              data-form-control="true"
                              className={styles.importanceSection}
                            >
                              <div className={styles.importanceLabel}>
                                {translator.prefilter_how_important}
                              </div>
                              <div>
                                <div className={styles.importanceRadioGroup}>
                                  <label className={styles.radioOption}>
                                    <input
                                      type="radio"
                                      name={`${filter.key}.must_have`}
                                      value="false"
                                      checked={!values[filter.key].must_have}
                                      onChange={() => {
                                        setFieldValue(
                                          `${filter.key}.must_have`,
                                          false,
                                        );
                                      }}
                                    />
                                    <span>
                                      {translator.prefilter_nice_to_have}
                                    </span>
                                  </label>
                                  <label className={styles.radioOption}>
                                    <input
                                      type="radio"
                                      name={`${filter.key}.must_have`}
                                      value="true"
                                      checked={values[filter.key].must_have}
                                      onChange={() => {
                                        setFieldValue(
                                          `${filter.key}.must_have`,
                                          true,
                                        );
                                      }}
                                    />
                                    <span>
                                      {translator.prefilter_must_have}
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className=" navigation-buttons-space clear">
            {currentPage > 1 && (
              <button
                type="button"
                onClick={() => handleBack()}
                className="btn-back flt-lft nav-btn1"
              >
                {translator.back_button}
              </button>
            )}

            {isFormValidForPage(values) && (
              <button type="submit" className="flt-rgt next-btn-active">
                {translator.next_button}
              </button>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default PreFilterForm;
